import { useAuth } from '@/contexts/AuthContext'
import { interactionNotificationService } from '@/services/interactionNotifications'
import { getPostsByAuthor } from '@/services/posts'
import type { Post } from '@/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUserPostInteractionsListener } from './useUserPostInteractionsListener'

export interface UseInteractionNotificationsOptions {
  enabled?: boolean
}

export interface UseInteractionNotificationsReturn {
  loading: boolean
  error: Error | null
}

export function useInteractionNotifications(
  options: UseInteractionNotificationsOptions = {},
): UseInteractionNotificationsReturn {
  const { enabled = true } = options
  const { userData } = useAuth()
  const [userPosts, setUserPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Fetch user's posts
  useEffect(() => {
    const fetchUserPosts = async () => {
      if (!userData?.id || !enabled) {
        setUserPosts([])
        return
      }

      try {
        setLoading(true)
        const posts = await getPostsByAuthor(userData.id)
        setUserPosts(posts || [])
      } catch (err) {
        console.error('Error fetching user posts:', err)
        setError(err instanceof Error ? err : new Error('Failed to fetch user posts'))
      } finally {
        setLoading(false)
      }
    }

    fetchUserPosts()
  }, [userData?.id, enabled])

  // Handle new interactions for notification creation
  const handleNewInteraction = useCallback(
    async (interaction: any, post: Post) => {
      try {
        // Avoid processing notifications for the user's own interactions
        if (interaction.userId === userData?.id) {
          return
        }

        await interactionNotificationService.createOrUpdateInteractionNotification(interaction, post)
      } catch (error) {
        console.error('Error creating interaction notification:', error instanceof Error ? error.message : error)
      }
    },
    [userData?.id],
  )

  // Use ref to maintain stable reference and prevent infinite loops
  const userPostsRef = useRef<Post[]>([])
  const lastPostIdsRef = useRef<string>('')

  // Only update the ref when post IDs actually change
  const currentPostIds = userPosts.map((post) => post.id).join(',')
  if (currentPostIds !== lastPostIdsRef.current) {
    userPostsRef.current = userPosts
    lastPostIdsRef.current = currentPostIds
  }

  // Re-enable with fixed implementation - disable for now to prevent loops
  const interactions: any[] = []
  const interactionsLoading = false
  const interactionsError = null

  return {
    loading: loading || interactionsLoading,
    error: error || interactionsError,
  }
}
